/**
 * Icon and display phrase guessing utility
 * Used by point discovery, CSV import, and other UI components
 */
import { BacnetPoint } from '../models/point';
import { TileIcon } from '../models/tile-icon';

export class IconGuesserUtil {
  // Default display phrases based on point name
  static getDisplayPhrases(pointName?: string): { on: string; off: string } {
    if (!pointName) return { on: 'On', off: 'Off' };

    const name = pointName.toLowerCase();

    // Common patterns
    if (name.includes('lock')) return { on: 'Locked', off: 'Unlocked' };
    if (name.includes('fan') || name.includes('pump')) return { on: 'Running', off: 'Stopped' };
    if (name.includes('door') || name.includes('window')) return { on: 'Open', off: 'Closed' };
    if (name.includes('alarm')) return { on: 'Active', off: 'Inactive' };
    if (name.includes('valve')) return { on: 'Open', off: 'Closed' };
    if (name.includes('enable')) return { on: 'Enabled', off: 'Disabled' };

    return { on: 'On', off: 'Off' };
  }

  // Icon selection based on point name/description
  static guessIcon(point: BacnetPoint | { Name: string; Description?: string }): TileIcon {
    const text = ('Description' in point ? point.Description : '') || point.Name || '';
    return this.guessIconFromText(text);
  }

  static guessIconFromText(text: string): TileIcon {
    if (!text) return 'dot-circle';

    const word = text.toLowerCase();

    // Temperature
    if (word.includes('temp')) return 'thermometer-three-quarters';
    if (word.includes('heat') && word.includes('cool')) return 'heat';
    if (word.includes('heat')) return 'fire';
    if (word.includes('cool') || word.includes('chill')) return 'snowflake';

    // Mechanical
    if (word.includes('fan')) return 'fan';
    if (word.includes('pump')) return 'pump';
    if (word.includes('valve')) return 'faucet';
    if (word.includes('pressure')) return 'tachometer-fastest';

    // Building
    if (word.includes('door')) return 'door-closed';
    if (word.includes('window')) return 'window-frame';
    if (word.includes('lock')) return 'lock-alt';
    if (word.includes('light')) return 'lightbulb';
    if (word.includes('alarm')) return 'bell';

    // Environmental
    if (word.includes('humid')) return 'humidity';
    if (word.includes('water')) return 'faucet-drip';
    if (word.includes('air')) return 'wind';
    if (word.includes('power') || word.includes('energy')) return 'bolt';

    return 'dot-circle';
  }

  // Deprecated alias
  static guessIconFromName(name: string): TileIcon {
    return this.guessIconFromText(name);
  }
}